<script setup lang="ts">
import { useElementSize } from '@vueuse/core';
import { useTemplateRef } from 'vue';

defineProps<{
  totalPrice: number;
  hasOnlyExpiredItems: boolean;
  hasPendingTransaction: boolean;
  isPaymentBlocked: boolean;
}>();

const emit = defineEmits<{
  pay: [];
  resume: [];
}>();

// On mobile the payment bar is fixed: its measured height reserves the same space in the page flow
const paymentBar = useTemplateRef<HTMLElement>('paymentBar');
const { height: paymentBarHeight } = useElementSize(paymentBar, undefined, { box: 'border-box' });
</script>

<template>
  <div class="payment-footer" :style="{ '--payment-bar-height': `${paymentBarHeight}px` }">
    <div ref="paymentBar" class="payment-container">
      <p v-if="hasOnlyExpiredItems" class="all-expired-message">
        {{ $t('cart.details.allExpired') }}
      </p>
      <button v-else-if="hasPendingTransaction" type="button" class="payment-button" @click="emit('resume')">
        {{ $t('cart.payment.resume') }}
      </button>
      <button v-else type="button" class="payment-button" :disabled="isPaymentBlocked" @click="emit('pay')">
        {{ totalPrice > 0 ? $t('cart.details.payment', { price: totalPrice }) : $t('cart.details.book') }}
      </button>
    </div>
  </div>
</template>

<style scoped lang="css">
.payment-footer {
  position: sticky;
  bottom: 0;
}

.payment-container {
  padding: var(--yb-spacing-base) 0;
  background: white;
  border-top: var(--yb-color-mediumgray) solid 1px;
}

/* Mobile: pinned to the bottom of the screen above the host page, like the mobile CTA of mode-details */
@media screen and (width < 1024px) {
  .payment-footer {
    position: static;
    height: var(--payment-bar-height);
  }

  .payment-container {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 999;
    padding: var(--yb-spacing-base);
    padding-bottom: calc(var(--yb-spacing-base) + env(safe-area-inset-bottom));
    border-top: none;
    box-shadow: var(--yb-details-bottom-box-shadow);
  }
}

.payment-button {
  width: 100%;
  height: 56px;
  color: var(--yb-button-text-color);
  background-color: var(--yb-color-primary);
  border: none;
  padding: var(--yb-spacing-sm);
  border-radius: var(--yb-radius-sm);
  cursor: pointer;
  font-size: var(--yb-text-base);
  font-weight: var(--yb-text-semi-bold);

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  &:active:not(:disabled) {
    opacity: 0.9;
  }
}

.all-expired-message {
  text-align: center;
  color: #92400e;
  font-size: var(--yb-text-sm);
  padding: var(--yb-spacing-sm);
  background: #fef3c7;
  border-radius: var(--yb-radius-sm);
  margin: 0;
}
</style>
